import { PlotOrStationSummary } from '@weenat/client/dist/resources/measurements'
import { Summary } from '@weenat/client/dist/resources/measurements.type'
import { useIntl } from '@weenat/wintl'
import { useOrgContext } from 'app/orgProvider'
import { useNavigate } from 'app/routx-router'
import Button from 'app/src/kit/Button'
import Card from 'app/src/kit/Card'
import Icons from 'app/src/kit/Icons'
import InfoCard from 'app/src/kit/InfoCard'
import Text from 'app/src/kit/Text'
import ViewKindSelector from 'app/src/kit/ViewKindSelector'
import MarkerLegendModal from 'app/src/map/MarkerLegendModal'
import DisplaySettings from 'app/src/myFarm/tools/DisplaySettings'
import MyFarmTools from 'app/src/myFarm/tools/MyFarmTools'
import { isEmpty } from 'lodash-es'
import isNil from 'lodash-es/isNil'
import { memo, useCallback } from 'react'
import styled from 'styled-components'
import Geolocation from '../../../map/Geolocation'
import ControlsContainer from './ControlsContainer'
import DashboardMapLoading from './DashboardMapLoading'
import MapControlsOverlay from './MapControlsOverlay'
import MapOverlay from './MapOverlay'
import Radar from './Radar/Radar'
import useIsRadarEnabled from './Radar/hooks/useIsRadarEnabled'
import ZoomButtons from './ZoomButtons'
import { elementIds } from './constants'
import {
  useBackgroundMapContext,
  useBackgroundMapDispatcher
} from './contexts/BackgroundMapContext'
import useDrawVisiblePlotShapes from './hooks/useDrawVisiblePlotShapes'
import useUniverseOptionsQuery from './universes/useUniverseOptionsQuery'

type PlotsMapControlsProps = {
  summaries: Summary[]
  filteredSummaries: Summary[]
}

const AddButton = styled(Button)`
  flex: 1;
  padding: 32px 16px;
`

const PlotsMapControls: FC<PlotsMapControlsProps> = memo(({ summaries, filteredSummaries }) => {
  const { t } = useIntl()
  const org = useOrgContext()
  const nav = useNavigate()

  const { universeOptions } = useUniverseOptionsQuery(org.id)

  const {
    api: { map, maps }
  } = useBackgroundMapContext()
  const dispatch = useBackgroundMapDispatcher()

  const [isRadarEnabled] = useIsRadarEnabled()

  const onGeolocationSuccess = useCallback<PositionCallback>(
    ({ coords: { latitude, longitude } }) => {
      dispatch({
        type: 'setGeolocation',
        newGeolocation: {
          lat: latitude,
          lng: longitude
        }
      })
    },
    [dispatch]
  )

  const navToDeviceAdd = () => nav(`/farms/${org.id}/admin/devices/add`)
  const navToPlotAdd = () => nav(`/farms/${org.id}/plots/create`)

  const isEmptySummary = isEmpty(summaries) && isEmpty(universeOptions)

  return (
    <MapControlsOverlay>
      {!isRadarEnabled ? (
        <Flex $flex={1} $flexDirection='column' $justifyContent='space-between'>
          {/* TOP */}
          <MyFarmTools
            summaries={summaries}
            filteredResults={filteredSummaries}
            displaySettingsElement={<DisplaySettings isWeatherMap={false} />}
            isPlotsMap
          />

          {/* FILLS THE SCREEN */}

          <Flex
            $flex={1}
            $flexDirection='column'
            $width='100%'
            $gap='xl'
            $zIndex={2}
            $pointerEvents={isEmptySummary ? 'auto' : undefined}
          >
            {isEmptySummary ? (
              <Card $m={'auto'} $gap={'xl'} $p='lg' $width={480} $overflowY='auto'>
                <Text $fontWeight='bold' $textAlign='center' $fontSize='md'>
                  {t('help.empty_map.welcome')}
                </Text>

                <Flex $flexDirection='column' $gap='lg' $minHeight={'max-content'}>
                  <Text $textAlign='start' $lineHeight='md'>
                    {t('help.empty_map.my_farm_explained')}
                  </Text>

                  <Flex $gap='lg' $width='100%'>
                    <AddButton variant='small' onPress={navToDeviceAdd}>
                      <Flex $flexDirection='column' $gap='md'>
                        <Text $fontWeight='medium' $fontSize='md'>
                          {t('models.device.actions.create_short')}
                        </Text>
                        <Icons.SingleSensorFilledAdd $size='2xl' $color='grayscale.white' />
                      </Flex>
                    </AddButton>

                    <AddButton variant='small' onPress={navToPlotAdd}>
                      <Flex $flexDirection='column' $gap='md'>
                        <Text $fontWeight='medium' $fontSize='md'>
                          {t('models.plot.actions.create')}
                        </Text>
                        <Icons.PlotsFilled $size='2xl' $color='grayscale.white' />
                      </Flex>
                    </AddButton>
                  </Flex>
                </Flex>

                <InfoCard
                  $mt='md'
                  title={t('single_words.tip')}
                  type='tip'
                  message={
                    <Text $color={'primary.800'} $fontSize='rg'>
                      {t('help.empty_map.tip.message_first_part')}
                      <Box
                        style={{
                          display: 'inline-block',
                          marginInlineStart: 4,
                          marginInlineEnd: 4
                        }}
                      >
                        <Icons.PlusSignCircle
                          $size='lg'
                          $p={'xs'}
                          $color={'grayscale.white'}
                          $backgroundColor={'primary.500'}
                        />
                      </Box>
                      {t('help.empty_map.tip.message_second_part', {
                        buttonPosition: t('help.empty_map.tip.inside_left_panel')
                      })}
                    </Text>
                  }
                  $borderRadius='md'
                />
              </Card>
            ) : (
              <>
                {/* SMASHED RIGHT */}
                <Flex
                  $flexDirection='column'
                  $gap='lg'
                  $justifyContent='center'
                  $alignItems='center'
                  $alignSelf='flex-end'
                  $flex={1}
                  $mt={80}
                >
                  <Flex $flexDirection='column' $gap='md'>
                    <ControlsContainer $flexDirection='column-reverse' $py='sm' $gap='sm'>
                      <ZoomButtons />
                    </ControlsContainer>

                    <ControlsContainer>
                      <Geolocation onSuccess={onGeolocationSuccess} tooltipPlacement='left' />
                    </ControlsContainer>

                    <Radar map={map} maps={maps} />

                    <ControlsContainer>
                      <MarkerLegendModal tooltipPlacement='left' />
                    </ControlsContainer>
                  </Flex>
                </Flex>

                {/* BOTTOM */}
                <ViewKindSelector />
              </>
            )}
          </Flex>
        </Flex>
      ) : (
        <Radar map={map} maps={maps} />
      )}
    </MapControlsOverlay>
  )
})

interface PlotsMapOverlayProps {
  currentItems: PlotOrStationSummary[]
  isLoading: boolean
  allSummaries: PlotOrStationSummary[]
}

const PlotsMapOverlay: FC<PlotsMapOverlayProps> = ({
  allSummaries: summaries,
  children,
  currentItems,
  isLoading
}) => {
  const org = useOrgContext()

  const {
    api: { map, maps }
  } = useBackgroundMapContext()

  const shouldShowControls = !isNil(map) && !isNil(maps)

  useDrawVisiblePlotShapes({ currentOrganizationId: org.id, summaries: currentItems })
  return (
    <MapOverlay id={elementIds.OverlayContainer} $flexDirection={'column'}>
      {isLoading ? (
        <DashboardMapLoading />
      ) : (
        <>
          {shouldShowControls ? (
            <PlotsMapControls
              // all summaries not only current
              summaries={summaries}
              filteredSummaries={currentItems}
            />
          ) : null}
          {children}
        </>
      )}
    </MapOverlay>
  )
}

export default PlotsMapOverlay
